/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 03.06.17.
 */

import React from "react";
import PropTypes from "prop-types";
import Form from "react-jsonschema-form";
import _, {property} from "underscore";

import {default as FormFieldTemplate} from "./Template";
import * as Field from "./Field";
import * as Widget from "./Widget";
import Schema from "../../../Schema";
import t from "../../../trans";
import {deepClone, deepEquals} from "../../../util";
import ErrorListTemplate from "./ErrorListTemplate";
import FormValidator from "./FormValidator";
import {SessionStore} from "../../../services/Session";

class ResourceForm extends React.Component {
    static propTypes = {
        /**
         * Location params
         */
        params: PropTypes.shape({
            path: PropTypes.string.isRequired,
            resource: PropTypes.string.isRequired,
            action: PropTypes.oneOf(["list","new","view","edit","delete"]).isRequired,
            id: PropTypes.string,
            query: PropTypes.object,
            parentPath: PropTypes.string,
            parentResource: PropTypes.string,
            parentAction: PropTypes.oneOf(["list","new","view","edit","delete"]),
            parentId: PropTypes.string,
            parentField: PropTypes.string
        }),
        /**
         * JSON Schema describing resource structure
         */
        schema: PropTypes.instanceOf(Schema).isRequired,
        /**
         * Current locale name
         */
        locale: PropTypes.string,
        /**
         * Form data values
         */
        formData: PropTypes.object,
        /**
         * Error information about the form data. Should be in the form:
         * {
         *      fieldName: errorMessage,
         *      fieldName: [errorMessage, ...]
         * }
         */
        errors: PropTypes.object,
        /**
         * Callback called when there is change in field value. Signature:
         * onChange(formData)
         */
        onChange: PropTypes.func,
        /**
         * Callback called when there is errors in form. Signature:
         * onError([{property: field, message: message}, ...])
         */
        onError: PropTypes.func,
        /**
         * Callback called when user clicks to the submit button. Signature:
         * onSubmit(formData)
         */
        onSubmit: PropTypes.func,
        location: PropTypes.object
    };

    static defaultProps = {
    };

    constructor(props) {
        super(props);
        let fixedSchema = this.fixSchema(props);
        this.state = {
            schema: fixedSchema.jsonSchema,
            uiSchema: fixedSchema.uiSchema,
            validator: this.getValidator(props, fixedSchema.jsonSchema, fixedSchema.uiSchema),
            context: {
                /**
                 * If the search form is expanded or not
                 */
                expanded: false,
                expandedCallback: this.onClickExpanded.bind(this),
                onSubmit: this.onSubmit.bind(this),
                locale: props.locale,
                params: props.params,
                location: props.location
            },
            isErrors: false,
            transformErrors: false
        };
        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.validateFormData = this.validateFormData.bind(this);
        this.transformErrors = this.transformErrors.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.schema !== this.props.schema) {
            let fixedSchema = this.fixSchema(nextProps);
            this.setState({
                schema: fixedSchema.jsonSchema,
                uiSchema: fixedSchema.uiSchema,
                validator: this.getValidator(nextProps, fixedSchema.jsonSchema, fixedSchema.uiSchema),
                context: {
                    expanded: this.state.context.expanded,
                    expandedCallback: this.onClickExpanded.bind(this),
                    onSubmit: this.onSubmit.bind(this),
                    locale: nextProps.locale,
                    params: nextProps.params,
                    location: nextProps.location
                }
            });
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            nextProps.schema !== this.props.schema ||
            nextProps.locale !== this.props.locale ||
            nextProps.formData !== this.props.formData ||
            nextProps.errors !== this.props.errors ||
            nextState !== this.state ||
            !deepEquals(nextProps.params, this.props.params)
        );
    }

    getValidator(props, jsonSchema, uiSchema) {
        let schema = new Schema(jsonSchema, uiSchema, null);
        return new FormValidator(props, schema);
    }

    fixSchema(props) {
        /**
         * @type {Schema}
         */
        let schema = props.schema;
        if (!schema) return {};
        let jsonSchema = schema.getJsonSchema();
        if (!jsonSchema) return {};
        jsonSchema = deepClone(jsonSchema);
        let uiSchema = schema.getUiSchema();
        if (!uiSchema) uiSchema = {};
        else uiSchema = deepClone(uiSchema);

        this.fixJsonSchema(schema, jsonSchema);
        this.fixUiSchema(schema, uiSchema);
        this.applyRestrictions(props.location, uiSchema, jsonSchema);
        return {
            jsonSchema: jsonSchema,
            uiSchema: uiSchema
        };
    }

    applyRestrictions(location, uiSchema, jsonSchema) {
        let order = uiSchema["ui:order"];
        if (!order) return;
        let restrictions = SessionStore.isAuthorized(location.pathname);
        if (!restrictions) return;
        if (_.isArray(restrictions)) {
            for (let r of restrictions) {
                if (r && !_.isEmpty(r.query)) {
                    restrictions = r;
                    break;
                }
            }
        }
        let allowedFields = restrictions.query;
        if (!allowedFields) return;

        order = _.filter(order, function (o) {
            return _.has(allowedFields, o);
        });
        uiSchema["ui:order"] = order;

        jsonSchema.properties = _.pick(jsonSchema.properties, _.keys(allowedFields));
    }

    /**
     * Modifitseerib skeemi järgmiselt:
     *  * Eemaldab properties, mida ei leidu uiSchema "ui:order" väljal (kui see on antud)
     *  * Lisab properties osasse väljad, mille nimi uiSchema "ui:order" väljal sissaldab kaldkriipsu
     * @param {Schema} schema
     * @param {JsonSchemaData} jsonSchema
     */
    fixJsonSchema(schema, jsonSchema) {
        let order = schema.getOrder();
        if (!order || order.length == 0) return;

        if (!jsonSchema.properties) jsonSchema.properties = {};

        jsonSchema.required = schema.getRequired();
        if (jsonSchema.required == null) jsonSchema.required = [];

        for (let field of order) {
            let fieldSchema = schema.field(field);
            if (field.indexOf("/") >= 0) {
                if (fieldSchema) {
                    jsonSchema.properties[field] = deepClone(fieldSchema.getJsonSchema());
                }
            }
            let fieldUiSchema = fieldSchema.getUiSchema();
            if (fieldUiSchema && fieldUiSchema["ui:valueMapping"] && _.isArray(fieldUiSchema["ui:valueMapping"])) {
                // Change value to array of values:
                jsonSchema.properties[field] = {
                    "title": jsonSchema.properties[field].title,
                    "titlePl": jsonSchema.properties[field].titlePl,
                    "type": "array",
                    "items": jsonSchema.properties[field]
                };
            }
            switch (fieldSchema.getType()) {
                case "object":
                    // For subfields we remove required field list:
                    if (jsonSchema.properties[field].required) delete jsonSchema.properties[field].required;

                    this.fixJsonSchema(fieldSchema, jsonSchema.properties[field]);
                    break;
                case "array":
                    if (jsonSchema.properties[field].items && jsonSchema.properties[field].items.type === "object")
                        this.fixJsonSchema(fieldSchema.field("*"), jsonSchema.properties[field].items);
                    break;
                default:
                    break;
            }
        }
        jsonSchema.properties = _.pick(jsonSchema.properties, ...order);
    }

    mapJsonSchema(jsonSchema, mapping) {
        return jsonSchema;
    }

    /**
     *
     * @param {Schema} schema
     * @param {JsonUiSchema} uiSchema
     */
    fixUiSchema(schema, uiSchema) {
        let fields = schema.getOrder();
        // All transformations assume that we have at least one form field!
        if (!fields || fields.length === 0) return;

        // If "ui:order" field is missing or empty then generate this from existing fields:
        if (!uiSchema["ui:order"]) {
            return;
            //uiSchema["ui:order"] = fields;
        }

        for (let field of fields) {
            let fieldSchema = schema.field(field);

            // Move hierahical field (ie fields whose names are containing slashes) descriptions into first level
            if (field.indexOf("/") >= 0) uiSchema[field] = fieldSchema.getUiSchema();
            else if (!uiSchema[field]) uiSchema[field] = {};

            // Transformations based by field widget type:
            switch (uiSchema[field]["ui:widget"]) {
                case "checkbox":
                    // Make all checkboxes reside inside string field.
                    uiSchema[field]["ui:field"] = "string";
                    break;
                default:
                    break;
            }

            // Transformations based by field value type:
            switch (fieldSchema.getType()) {
                case "string":
                    // Automatically use textarea widget for string fields with size > 100
                    if ((fieldSchema.getSize() > 100 || fieldSchema.getContentMediaType()) && !uiSchema[field]["ui:widget"]) {
                        uiSchema[field]["ui:widget"] = "textarea";
                    }
                    break;
                case "object":
                case "array":
                    // Recursively fix subschema:
                    this.fixUiSchema(fieldSchema, uiSchema[field]);
                    break;
                default:
                    break;
            }
        }

        // Implement expandable template
        if (uiSchema["ui:field"] === "expandable") {
            let isFirst = true;
            for (let field of fields) {
                uiSchema[field]["ui:template"] = (isFirst ? "expandable" : "expandableSibling");
                isFirst = false;
            }
        }

    }

    onClickExpanded() {
        this.setState({context: {
            expanded: !this.state.context.expanded,
            expandedCallback: this.state.context.expandedCallback,
            onSubmit: this.state.context.onSubmit
        }})
    }

    onChange(param) {
        if (this.props.onChange) {
            this.props.onChange(this.fixFormDataBack(param.formData));
        }
    }

    onSubmit(param) {
        if (this.props.onSubmit) {
            this.props.onSubmit(this.fixFormDataBack(param.formData));
        }
    }

    /**
     * Transforms form data from Form representation to REST API representation
     * @param formData
     */
    fixFormDataBack(formData) {
        if (!formData) return {};
        var data = {};
        for (let key of Object.keys(formData)) {
            if (this.state.uiSchema[key] && this.state.uiSchema[key]["ui:valueMapping"]) {
                let result = this.mapValueFromForm(key, this.state.uiSchema[key]["ui:valueMapping"], formData[key], this.state.schema.properties[key]);
                // data = _.extend(data, result);
                data = {...data, ...result};
            }
            else if (!_.has(data, key)) {
                data[key] = this.fixValueBack(formData[key], this.state.schema.properties[key]);
            }
        }
        // Recreate hierarchical structure:
        for (let key of this.state.uiSchema["ui:order"]) {
            if (key.indexOf("/") >= 0) {
                let keyItems = key.split("/");
                let data2 = data;
                for (let i = 0; i < keyItems.length-1; i++) {
                    if (!_.has(data2, keyItems[i])) data2[keyItems[i]] = {};
                    data2 = data2[keyItems[i]];
                }
                data2[keyItems[keyItems.length-1]] = formData[key];
                delete data[key];
            }
        }
        return data;
    }

    fixValueBack(value, schema) {
        if (value == null) {
            if (!schema) return null;
            switch (schema["type"]) {
                case "object":
                    return {};
                case "array":
                    return [];
                case "integer":
                case "number":
                    return null;
                default:
                    return null;
            }
        }
        return value;
    }

    /**
     * Transforms form data from REST API representation to Form representation
     * @param formData
     */
    fixFormData(formData) {
        if (!formData || _.isEmpty(formData)) return {};
        var fData = {};
        for (let key of this.state.uiSchema["ui:order"]) {
            let value = null;
            if (key.indexOf("/") >= 0) {
                // Move value to appropriate place:
                let keyItems = key.split("/");
                value = formData;
                while (keyItems.length > 0) {
                    let item = keyItems.shift();
                    if (_.has(value, item)) value = value[item];
                    else {
                        value = null;
                        break;
                    }
                }
            } else {
                value = formData[key];
            }
            fData[key] = this.fixValue(value, this.state.schema.properties[key]);
        }
        for (let key of _.keys(formData)) {
            fData[key] = this.fixValue(formData[key], this.state.schema.properties[key]);
        }
        for (let key of _.keys(this.state.uiSchema)) {
            if (this.state.uiSchema[key]["ui:valueMapping"]) {
                fData[key] = this.mapValueToForm(key, this.state.uiSchema[key]["ui:valueMapping"], formData, this.state.schema.properties);
            }
        }
        return fData;
    }

    fixValue(value, schema) {
        if (schema) {
            switch (schema.type) {
                case "integer":
                    if (_.isString(value)) value = parseInt(value);
                    break;
                case "boolean":
                    if (_.isString(value)) value = (Boolean(value)).valueOf();
                    break;
                case "string":
                    if (value != null && schema.format === "time") {
                        // TODO: ajv validator does not support timezone and milliseconds in "time" format, removing this
                        let idx = value.indexOf("+");
                        if (idx === -1) idx = value.indexOf("Z");
                        if (idx >= 0) value = value.substring(0, idx);
                        idx = value.indexOf(".");
                        if (idx >= 0) value = value.substring(0, idx);
                    }
                    break;
                case "array":
                    // Traverse over array items and fix them:
                    if (value == null) value = [];
                    if (_.isArray(value)) {
                        let newValue = [];
                        for (let v of value) {
                            newValue.push(this.fixValue(v, schema.items));
                        }
                        value = newValue;
                    }
                    break;
                default:
                    break;
            }
        }
        return (value === "" || value == null ? undefined : value);
    }

    mapValueToForm(key, mapping, formData, schema) {
        if (_.isArray(mapping)) {
            let result = [];
            for (let field of mapping) {
                result.push(this.fixValue(formData[field], schema[field]));
            }
            return result;
        }
        return this.fixValue(formData[key], schema[key]);
    }

    mapValueFromForm(key, mapping, value, schema) {
        if (_.isArray(mapping)) {
            let result = {};
            for (let i = 0; i < mapping.length; i++) {
                result[mapping[i]] = this.fixValueBack(value[i], schema.items);
            }
            return result;
        }
        return {[key]: this.fixValueBack(value, schema)};
    }

    validateFormData(formData, errors) {
        let isErrors = this.state.validator.validate(formData, errors);
        if (this.props.errors) {
            // isErrors = true;
            for (let field of _.keys(this.props.errors)) {
                let error = this.props.errors[field];
                if (_.indexOf(this.state.uiSchema["ui:order"], field) === -1) {
                    // This field does not exist in form, show this error in global place:
                    if (field != null && field !== "") error = field + ": " + error;
                    field = null;
                }
                if (_.isArray(error)) {
                    for (let e of error) {
                        if (field == null || field === "") {
                            errors.addError(e);
                        } else
                            if (errors[field]) errors[field].addError(e);
                    }
                } else {
                    if (field == null || field === "") {
                        errors.addError(error);
                    } else
                        if (errors[field]) errors[field].addError(error);
                }
            }
        }
        this.onError(isErrors);

        return errors;
    }

    onError(isErrors) {
        if (this.props.onError) this.props.onError(isErrors);
    }

    containsError(errors) {
        if (!errors || !errors.__errors) return false;
        if (errors.__errors.length > 0) return true;
        for (let k of _.keys(errors)) {
            if (_.isObject(errors[k]) && errors[k].__errors && errors[k].__errors.length > 0) return true;
            if (this.containsError(errors[k])) return true;
        }
        return false;
    };


    /**
     * Translates error messages (both JSON Schema Form messages and messages from REST API)
     * @param {Array} errors
     * @returns {Array}
     */
    transformErrors(errors) {
        if (errors) {
            let transformedErrors = [];
            for (let e of errors) {
                if (e.name === "required" && e.params) {
                    // Transform this error behind the field:
                    /*
                    let propertyName = e.argument;
                    e.argument = null;
                    e.instance = undefined;
                    e.message = t("value is required");
                    e.property += "." + propertyName;
                    e.schema = e.schema.properties[propertyName];
                    e.stack = propertyName + " " + e.message;
                    */
                    // transformedErrors.push({property: e.property + "." + e.argument, message: t("value is required")});
                } else if (e.name === "format" && e.params && (e.params.format === "date-time" || e.params.format === "time")) {
                    // Ignore date-time or time format errors, because they require timezone specifier
                    // TODO: Implement date-time and time format checking here
                } else if (e.name === "type" && e.message === 'should be string' && (e.property === '.created[0]' || e.property === '.created[1]')) {
                    // Workaround for the case where auditTrail search timestamps are not filled
                } else {
                    transformedErrors.push({property: e.property, message: t(e.message)});
                }
            }
            errors = transformedErrors;
        }
        return errors;
    }

    render() {
        return (
            <Form className="form-horizontal"
                  name="resource"
                  onChange={this.onChange}
                  onSubmit={this.onSubmit}
                  schema={this.state.schema}
                  uiSchema={this.state.uiSchema}
                  formData={this.fixFormData(this.props.formData)}
                  validate={this.validateFormData}
                  fields={Field}
                  widgets={Widget}
                  FieldTemplate={FormFieldTemplate}
                  formContext={this.state.context}
                  showErrorList={true}
                  ErrorList={ErrorListTemplate}
                  liveValidate={true}
                  transformErrors={this.transformErrors}
            >
                <div/>
            </Form>
        );
    }
}

export default ResourceForm;
